import { XIcon } from "lucide-react";
import { useTheme } from "next-themes";
import { Toaster as Sonner, type ToasterProps } from "sonner";

import { Spinner } from "@/components/ui/spinner";

/*
 * Sonner, unstyled and redrawn as Geist's toast: 420px wide, 24px from the
 * corner, 16px padding, 12px corners, the menu shadow and no border, 14px
 * text on a 21px line. A plain toast is the page colour; success is blue-700,
 * error red-800, warning amber-800 — the whole toast, not an icon, carries
 * the type, so there are no icons (bar the spinner on a loading toast).
 * Dismiss is a 32px tertiary X at the right of the message.
 *
 * Sonner's stack already behaves like Geist's — older toasts peek above the
 * front one and shrink 5% a step — so only the gap (20px) and the timeout
 * (3.5s) change.
 *
 * `richColors` is ignored: its attribute-selector colours would outrank
 * these, and Geist's type colours are the rich colours.
 */
const Toaster = ({ richColors: _richColors, toastOptions, ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      gap={20}
      offset={24}
      mobileOffset={{ left: 24, right: 24, bottom: "max(24px, env(safe-area-inset-bottom))" }}
      duration={3500}
      visibleToasts={3}
      closeButton
      icons={{
        loading: <Spinner size="sm" className="text-current" />,
        close: <XIcon className="size-4" />,
      }}
      style={{ "--width": "420px" } as React.CSSProperties}
      toastOptions={{
        unstyled: true,
        ...toastOptions,
        classNames: {
          toast:
            "group/toast flex w-(--width) max-w-[calc(100vw-48px)] items-center gap-4 rounded-xl bg-[var(--geist-background)] p-4 pr-14 font-sans text-[14px] text-gray-1000 leading-[21px] shadow-menu data-[expanded=false]:data-[front=false]:*:opacity-0",
          success: "bg-blue-700! text-white!",
          error: "bg-red-800! text-white!",
          warning: "bg-amber-800! text-gray-1000!",
          content: "flex min-h-8 min-w-0 flex-1 flex-col justify-center gap-0.5",
          title: "text-pretty font-normal",
          description: "text-pretty opacity-80",
          icon: "hidden group-data-[type=loading]/toast:flex",
          closeButton:
            "absolute top-1/2 right-4 flex size-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-current transition-colors duration-150 hover:bg-gray-alpha-200 focus-visible:shadow-focus-ring outline-none",
          actionButton:
            "inline-flex h-8 shrink-0 cursor-pointer items-center rounded-md bg-gray-1000 px-3 font-medium text-background-100 text-sm hover:bg-[#383838] dark:hover:bg-[#ccc]",
          cancelButton:
            "inline-flex h-8 shrink-0 cursor-pointer items-center rounded-md px-3 font-medium text-current text-sm hover:bg-gray-alpha-200",
          ...toastOptions?.classNames,
        },
      }}
      {...props}
    />
  );
};

export { Toaster };
